import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import SavedPostsLimitWarning from "../components/SavedPostsLimitWarning";
import DailyLimitWarning from "../components/DailyLimitWarning";
import {
  DEFAULT_POST_LENGTH,
  DEFAULT_POST_STYLES,
  POST_LENGTHS,
  POST_STYLES,
} from "../constants/postSettings";
import {
  FiChevronUp,
  FiChevronDown,
  FiRefreshCw,
  FiSend,
} from "react-icons/fi";

// Import custom hooks
import usePostGeneration from "./hooks/usePostGeneration";
import usePublishing from "./hooks/usePublishing";
import { useDomainConfig } from "../hooks/useDomainConfig";

// Import components
import GeneratedPost from "./components/GeneratedPost";
import BasicInfoSection from "../components/BasicInfoSection";
import ProjectInfoSection from "../components/ProjectInfoSection";
import ImageSourceSelector from "../components/ImageSourceSelector";

// Import utils
import {
  savePostToFirebase,
  countSavedPosts,
  incrementCreatedPostCount,
  getCreatedPostCount,
} from "./utils/firebaseUtils";
import { initFacebookSDK } from "./utils/facebookUtils";
import {
  handleFiles,
  suggestTopics as suggestTopicsLogic,
  savePost,
  getFanpageInfo,
  handleGeneratePost as generatePostLogic,
  handleRegeneratePost as regeneratePostLogic,
  handleRegenerateImages as regenerateImagesLogic,
  loadSavedPostData,
} from "./utils/normalPostGeneratorLogic";
import { getOptimizedToastContainerProps } from "../utils/toastUtils";

// Import constants
import { PublishingPlatforms } from "../constants/sharedTypes";
import { getDatabase, ref, get, onValue } from "firebase/database";
import { getDomainConfig } from "../constants/domains";

const NormalPostGenerator = () => {
  // Domain configuration hook
  const { domainConfig, switchDomain, refreshKey, isLoading, selectedDomain } =
    useDomainConfig();

  const [hasSavedDataLoaded, setHasSavedDataLoaded] = useState(false);

  // State for form inputs - Initialize with domain defaults
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [selectedContentType, setSelectedContentType] = useState("");
  const [referenceUrl, setReferenceUrl] = useState(""); // State cho URL tham khảo

  // Lấy danh sách các key từ PublishingPlatforms
  const platformOptions = Object.keys(PublishingPlatforms);

  // Tạo state với giá trị mặc định là platform đầu tiên
  const [selectedPublishingPlatform, setSelectedPublishingPlatform] = useState({
    text: platformOptions[0],
    id: PublishingPlatforms[platformOptions[0]],
  });

  const [targetCustomer, setTargetCustomer] = useState("");
  const [requirements, setRequirements] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [allowNoImage, setAllowNoImage] = useState(false);
  const [postLength, setPostLength] = useState(DEFAULT_POST_LENGTH);
  const [postStyles, setPostStyles] = useState(
    DEFAULT_POST_STYLES.length > 0 ? [DEFAULT_POST_STYLES[0]] : []
  );
  const [includeContactInfo, setIncludeContactInfo] = useState(false);
  const [imageSource, setImageSource] = useState("internet");
  const [contextFiles, setContextFiles] = useState([]);
  const [isProcessingContext, setIsProcessingContext] = useState(false);
  const [savedPostsCount, setSavedPostsCount] = useState(0);
  const [dailyPostCount, setDailyPostCount] = useState(0); // NEW: Track daily post count

  // State for UI controls
  const [fanpages, setFanpages] = useState([]);
  const [selectedFanpages, setSelectedFanpages] = useState([]);
  const [hasFanpages, setHasFanpages] = useState(true); // Trạng thái kiểm tra có fanpage hay không
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [isSuggestingTopics, setIsSuggestingTopics] = useState(false);
  const [postCount, setPostCount] = useState(0);
  const [postedCount, setPostedCount] = useState(0);

  // State for accordion sections
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    projectInfo: true,
    customization: true,
  });

  // State for post style display mode
  const [styleDisplayMode, setStyleDisplayMode] = useState("list"); // Only support list mode

  // State for drag and drop
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);

  // Refs
  const navigate = useNavigate();
  const generatedPostRef = useRef(null);
  const projectTypeDropdownRef = useRef(null);
  const contentTypeDropdownRef = useRef(null);
  const publishingPlatformDropdownRef = useRef(null);
  const contextFileInputRef = useRef(null);
  const suggestedTopicsRef = useRef(null);

  // ✅ ADD: Memoized styles để tránh re-render
  const dropdownStyle = useMemo(
    () => ({
      transition: "all 0.2s ease-in-out",
      transformOrigin: "top center",
    }),
    []
  );

  const transitionStyle = useMemo(
    () => ({
      transform: "none",
      willChange: "transform, opacity",
      backfaceVisibility: "hidden",
    }),
    []
  );

  // Custom hooks
  const {
    isGeneratingPost,
    isRegeneratingPost,
    isRegeneratingImage,
    generatedPost,
    editedPostText,
    setEditedPostText,
    hasEdited,
    setHasEdited,
    serperImages,
    selectedSerperImages,
    setSelectedSerperImages,
    geminiGeneratedImage,
    isEditMode,
    toggleEditMode,
    generateNewPost,
    regeneratePost,
    regenerateImages,
    checkPostLimit,
    generatedPosts,
    selectedPostIndex,
    selectPost,
    autoSavePostId,
  } = usePostGeneration();

  const { isPublishing, publishPost, publishToProfile } = usePublishing();

  // State for saving post
  const [isSaving, setIsSaving] = useState(false);

  // Đảm bảo postLength luôn có giá trị valid
  useEffect(() => {
    if (!postLength || !POST_LENGTHS.find((p) => p.value === postLength)) {
      console.log(
        "🔧 NormalPostGenerator: Setting fallback postLength to DEFAULT_POST_LENGTH"
      );
      setPostLength(DEFAULT_POST_LENGTH);
    }
  }, [postLength]);

  // ✅ ADD: Memoized AccordionSection component để tránh re-render không cần thiết
  const AccordionSection = useMemo(() => {
    return ({ title, isExpanded, toggleExpand, children, icon }) => {
      return (
        <div className="mb-3 md:mb-4 border border-gray-200 rounded-md overflow-visible">
          <div
            className="flex justify-between items-center p-3 md:p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={(e) => toggleExpand(e)}
          >
            <div className="flex items-center">
              {icon && <span className="mr-2">{icon}</span>}
              <h3 className="font-medium text-gray-800">{title}</h3>
            </div>
            <div>
              {isExpanded ? (
                <FiChevronUp className="text-gray-500" />
              ) : (
                <FiChevronDown className="text-gray-500" />
              )}
            </div>
          </div>
          <div
            className={`transition-all duration-300 ${
              isExpanded
                ? "max-h-[2000px] opacity-100"
                : "max-h-0 opacity-0 overflow-hidden"
            }`}
            style={{
              position: "relative",
              ...transitionStyle,
            }}
          >
            <div className="p-3 md:p-4 border-t border-gray-200">
              {children}
            </div>
          </div>
        </div>
      );
    };
  }, [transitionStyle]);

  // ✅ ADD: useCallback cho toggleSection để tránh re-render
  const toggleSection = useCallback((section, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // ✅ ADD: useCallback cho handleTopicSelect để tránh re-render
  const handleTopicSelect = useCallback((topic) => {
    setRequirements(topic);
  }, []);

  // ✅ ADD: useCallback cho clearSuggestedTopics để tránh re-render
  const clearSuggestedTopics = useCallback(() => {
    setSuggestedTopics([]);
  }, []);

  // Xử lý khi người dùng click vào khu vực dropzone
  const handleDropzoneClick = (event) => {
    // Kiểm tra xem người dùng có đang click vào label hoặc input không
    const target = event.target;
    const isLabel = target.tagName === "LABEL" || target.closest("label");
    const isInput = target.tagName === "INPUT";

    // Chỉ kích hoạt click vào input file nếu người dùng không click vào label hoặc input
    if (!isLabel && !isInput && contextFileInputRef.current) {
      contextFileInputRef.current.click();
    }
  };

  // Fetch counts
  const fetchPostCount = async () => {
    try {
      // Lấy số lượng bài viết đã tạo từ Firebase
      const totalCount = await getCreatedPostCount("total");
      const dailyCount = await getCreatedPostCount("daily");

      console.log(
        `📊 Thống kê bài viết đã tạo - Tổng: ${totalCount}, Hôm nay: ${dailyCount}`
      );
      setPostCount(totalCount);
      setDailyPostCount(dailyCount); // NEW: Update daily count for warning display
    } catch (error) {
      console.error("❌ Error fetching post count:", error);
      setPostCount(0);
      setDailyPostCount(0);
    }
  };

  const fetchPostedCount = async () => {
    try {
      // Implement your posted count fetching logic here
      // This is a placeholder
      setPostedCount(0);
    } catch (error) {
      console.error("Error fetching posted count:", error);
    }
  };

  const incrementPostCount = async () => {
    try {
      // Lưu số lượng bài viết đã tạo lên Firebase để thống kê
      const result = await incrementCreatedPostCount();
      if (result.success) {
        console.log("✅ Đã tăng count tạo bài viết thành công:", result);
        // Cập nhật state local để hiển thị ngay lập tức
        setPostCount(result.totalCount);
        setDailyPostCount(result.dailyCount); // ✅ Cập nhật dailyPostCount
      } else {
        console.error("❌ Lỗi khi tăng count tạo bài viết:", result.error);
      }
    } catch (error) {
      console.error("❌ Error incrementing post count:", error);
    }
  };

  const incrementPostedCount = async () => {
    try {
      // Implement your posted count incrementing logic here
      setPostedCount((prevCount) => prevCount + 1);
    } catch (error) {
      console.error("Error incrementing posted count:", error);
    }
  };

  // Fetch saved posts count
  const fetchSavedPostsCount = async () => {
    try {
      const count = await countSavedPosts();
      setSavedPostsCount(count);
    } catch (error) {
      console.error("Error fetching saved posts count:", error);
    }
  };

  useEffect(() => {
    console.log("Requirements changed to:", requirements);
  }, [requirements]);

  // ✅ ADD: Debug effect cho postLength
  useEffect(() => {
    console.log("🔍 Debug postLength:", {
      postLength,
      DEFAULT_POST_LENGTH,
      POST_LENGTHS: POST_LENGTHS.map((p) => p.value),
      isMatching: POST_LENGTHS.some((p) => p.value === postLength),
    });
  }, [postLength]);

  // Theo dõi sự thay đổi của selectedPublishingPlatform để cập nhật imageSource và tham số "không dùng ảnh"
  useEffect(() => {
    // Kiểm tra nếu PublishingPlatforms là 2 (Đăng bài PR lên các trang website) hoặc 4 (Tạo nội dung Email Marketing)
    if (
      selectedPublishingPlatform &&
      (selectedPublishingPlatform.id === 2 ||
        selectedPublishingPlatform.id === 4)
    ) {
      // Đặt nguồn ảnh mặc định là "Thiết bị" (upload)
      setImageSource("upload");

      // Đặt tham số "không dùng ảnh" là true
      setAllowNoImage(true);
    }
  }, [selectedPublishingPlatform]);

  useEffect(() => {
    if (localStorage.getItem("userAccessToken") == null) {
      navigate("/");
      return;
    }

    // ✅ FIXED: Load saved post data first, không pass domain defaults vào
    const hasSavedData = loadSavedPostData(
      setSelectedProjectType,
      setSelectedContentType,
      setSelectedPublishingPlatform,
      setTargetCustomer,
      setRequirements,
      setPostLength,
      setPostStyles,
      setIncludeContactInfo,
      setAllowNoImage,
      setImageSource,
      setReferenceUrl,
      {}, // ✅ Không pass domain defaults vào đây nữa
      PublishingPlatforms
    );

    // ✅ Log để biết có saved data hay không
    if (hasSavedData) {
      console.log("📦 NormalPostGenerator: Loaded saved post data");
      setHasSavedDataLoaded(true);
    } else {
      console.log(
        "🆕 NormalPostGenerator: No saved data, will use domain defaults"
      );
      setHasSavedDataLoaded(false);
    }

    // Fetch fanpages from Firebase instead of Facebook API
    const getFanpages = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const db = getDatabase();
        const connectedFanpagesRef = ref(
          db,
          `users/${userId}/connectedFanpages`
        );
        const connectedFanpagesSnapshot = await get(connectedFanpagesRef);

        if (connectedFanpagesSnapshot.exists()) {
          const connectedData = connectedFanpagesSnapshot.val();
          const fanpageList = Object.values(connectedData);

          console.log("Fanpages loaded from Firebase:", fanpageList);

          if (fanpageList.length > 0) {
            setFanpages(fanpageList);
            setHasFanpages(true);

            // Nếu chỉ có 1 fanpage, tự động chọn fanpage đó
            if (fanpageList.length === 1) {
              setSelectedFanpages([fanpageList[0]]);
            } else {
              // Nếu có nhiều fanpage, mặc định không chọn fanpage nào
              setSelectedFanpages([]);
            }
          } else {
            setHasFanpages(false);
            setFanpages([]);
            setSelectedFanpages([]);
          }
        } else {
          console.log("Không tìm thấy fanpage nào trong Firebase");
          setHasFanpages(false);
          setFanpages([]);
          setSelectedFanpages([]);
        }
      } catch (error) {
        console.error("Error fetching fanpages from Firebase:", error);
        setHasFanpages(false);
        setFanpages([]);
        setSelectedFanpages([]);
      }
    };

    getFanpages();
    fetchPostCount();
    fetchPostedCount();
    fetchSavedPostsCount();

    // Log thông tin để debug
    console.log(
      "🔄 NormalPostGenerator: Component mounted, fetching initial counts..."
    );
  }, [navigate]); // ✅ Chỉ depend on navigate

  useEffect(() => {
    const handleClickOutside = (event) => {
      const targetElement = event.target;

      // Kiểm tra nếu click vào textarea hoặc là phần tử con của textarea
      if (
        targetElement.tagName === "TEXTAREA" ||
        targetElement.closest("textarea")
      ) {
        event.stopPropagation();
        return;
      }

      // Kiểm tra nếu click vào dropdown fanpage hoặc là phần tử con của dropdown fanpage
      if (targetElement.closest('[data-dropdown="fanpage"]')) {
        return;
      }

      // Kiểm tra tất cả các dropdown và chỉ đóng khi click outside
      const isOutsideProjectTypeDropdown =
        projectTypeDropdownRef.current &&
        !projectTypeDropdownRef.current.contains(targetElement);
      const isOutsideContentTypeDropdown =
        contentTypeDropdownRef.current &&
        !contentTypeDropdownRef.current.contains(targetElement);
      const isOutsidePublishingPlatformDropdown =
        publishingPlatformDropdownRef.current &&
        !publishingPlatformDropdownRef.current.contains(targetElement);

      if (
        isOutsideProjectTypeDropdown &&
        isOutsideContentTypeDropdown &&
        isOutsidePublishingPlatformDropdown
      ) {
        setActiveDropdown(null);
      }

      // Chỉ đóng dropdown fanpage khi click outside và không phải là click vào nút toggle
      if (
        isDropdownOpen &&
        !targetElement.closest('[data-dropdown-toggle="fanpage"]')
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // ✅ ADD: useCallback cho toggleDropdown để tránh re-render
  const toggleDropdown = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      // Đóng các dropdown khác khi mở dropdown này
      const newValue = activeDropdown !== "fanpage";
      setActiveDropdown(newValue ? "fanpage" : null);
      setIsDropdownOpen(newValue);
      console.log("Toggle dropdown:", newValue);
    },
    [activeDropdown]
  );

  // ✅ ADD: useCallback cho handleFanpageSelect để tránh re-render
  const handleFanpageSelect = useCallback(
    (fanpage, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      console.log("Selecting fanpage:", fanpage);

      if (!fanpage || !fanpage.id) {
        console.error("Invalid fanpage object:", fanpage);
        return;
      }

      try {
        if (
          selectedFanpages &&
          selectedFanpages.find &&
          selectedFanpages.find((fp) => fp.id === fanpage.id)
        ) {
          setSelectedFanpages(
            selectedFanpages.filter((fp) => fp.id !== fanpage.id)
          );
          console.log("Unselected fanpage:", fanpage.name);
        } else {
          // Replace the selection, don't add
          setSelectedFanpages([fanpage]); // Set selectedFanpages to only the new fanpage
          console.log("Selected fanpage:", fanpage.name);
        }

        // Ẩn dropdown sau khi chọn fanpage
        setIsDropdownOpen(false);
        setActiveDropdown(null);
      } catch (error) {
        console.error("Error selecting fanpage:", error);
      }
    },
    [
      selectedFanpages,
      setSelectedFanpages,
      setIsDropdownOpen,
      setActiveDropdown,
    ]
  );

  // Generate post
  const handleGeneratePost = async () => {
    // System is now free to use - no plan check needed

    // Lấy thông tin fanpage
    const fanpageInfo = getFanpageInfo(selectedFanpages);

    // Debugging code to check requirements value
    console.log("Requirements value:", requirements);
    console.log("Requirements type:", typeof requirements);
    console.log(
      "Requirements trimmed:",
      requirements ? requirements.trim() : null
    );
    console.log("Context files:", contextFiles);

    // Kiểm tra xem người dùng đã cung cấp ít nhất một trong ba mục: Yêu cầu nội dung, URL tham khảo, hoặc Tài liệu tham khảo
    const hasRequirements = requirements && requirements.trim() !== "";
    const hasReferenceUrl = referenceUrl && referenceUrl.trim() !== "";
    const hasContextFiles = contextFiles && contextFiles.length > 0;

    if (!hasRequirements && !hasReferenceUrl && !hasContextFiles) {
      // Đảm bảo section projectInfo được mở ra để người dùng nhìn thấy các trường nhập liệu
      if (!expandedSections.projectInfo) {
        setExpandedSections((prev) => ({
          ...prev,
          projectInfo: true,
        }));
      }

      toast.error(
        "Vui lòng cung cấp ít nhất một trong ba mục: Yêu cầu nội dung hoặc Tài liệu tham khảo hoặc URL tham khảo",
        {
          position: "top-right",
          autoClose: 5000,
          icon: "ℹ️",
        }
      );
      return;
    }

    // Chắc chắn requirements sẽ luôn có ít nhất một chuỗi không rỗng
    let finalRequirements = "";

    if (hasRequirements) {
      finalRequirements = requirements.trim();
    } else if (hasReferenceUrl) {
      finalRequirements = "Tạo bài viết dựa trên URL tham khảo";
    } else if (hasContextFiles) {
      finalRequirements = "Tạo bài viết dựa trên tài liệu tham khảo đã tải lên";
    }

    // Chuẩn bị tham số cho API
    const options = {
      // Domain information
      domainId: domainConfig?.id,
      domainName: domainConfig?.name,

      // Form data
      projectType: selectedProjectType,
      contentType: selectedContentType,
      publishingPlatform:
        selectedPublishingPlatform && selectedPublishingPlatform.id
          ? selectedPublishingPlatform.id
          : typeof selectedPublishingPlatform === "string"
          ? selectedPublishingPlatform
          : 1,
      targetCustomer: targetCustomer,
      requirements: finalRequirements,
      postLength: postLength,
      postStyles: postStyles.join(", "),
      includeContactInfo: includeContactInfo,
      fanpageInfo: fanpageInfo,
      referenceUrl: referenceUrl, // Thêm URL tham khảo
    };

    console.log("Final options:", options);

    const userId = localStorage.getItem("userId");

    await generatePostLogic(
      options,
      userId,
      allowNoImage,
      imageSource,
      contextFiles,
      incrementPostCount,
      { generatedPostRef },
      generateNewPost
    );
  };

  // Handle regenerate post
  const handleRegeneratePost = async () => {
    // System is now free to use - no plan check needed

    // Lấy thông tin fanpage
    const fanpageInfo = getFanpageInfo(selectedFanpages);

    // Xác định requirements dựa trên các thông tin có sẵn
    const hasRequirements = requirements && requirements.trim() !== "";
    const hasReferenceUrl = referenceUrl && referenceUrl.trim() !== "";
    const hasContextFiles = contextFiles && contextFiles.length > 0;

    let finalRequirements = "";

    if (hasRequirements) {
      finalRequirements = requirements.trim();
    } else if (hasReferenceUrl) {
      finalRequirements = "Tạo bài viết dựa trên URL tham khảo";
    } else if (hasContextFiles) {
      finalRequirements = "Tạo bài viết dựa trên tài liệu tham khảo đã tải lên";
    }

    // Chuẩn bị options
    const options = {
      // Domain information
      domainId: domainConfig?.id,
      domainName: domainConfig?.name,

      // Form data
      projectType: selectedProjectType,
      contentType: selectedContentType,
      publishingPlatform:
        selectedPublishingPlatform && selectedPublishingPlatform.id
          ? selectedPublishingPlatform.id
          : typeof selectedPublishingPlatform === "string"
          ? selectedPublishingPlatform
          : 1,
      targetCustomer: targetCustomer,
      requirements: finalRequirements,
      postLength: postLength,
      postStyles: postStyles.join(", "),
      imageSource: allowNoImage ? "none" : imageSource,
      includeContactInfo: includeContactInfo,
      fanpageInfo: fanpageInfo,
      referenceUrl: referenceUrl, // Thêm URL tham khảo
    };

    const userId = localStorage.getItem("userId");

    await regeneratePostLogic(options, userId, regeneratePost);
  };

  // Handle regenerate images
  const handleRegenerateImages = async () => {
    // System is now free to use - no plan check needed

    const userId = localStorage.getItem("userId");
    await regenerateImagesLogic(
      userId,
      allowNoImage,
      imageSource,
      regenerateImages
    );
  };

  // Handle publish post
  const handlePublishPost = async () => {
    // System is now free to use - no plan check needed

    await publishPost(
      generatedPost,
      selectedFanpages,
      includeContactInfo,
      allowNoImage,
      imageSource,
      selectedImages,
      selectedSerperImages,
      geminiGeneratedImage,
      incrementPostedCount,
      autoSavePostId
    );
  };

  // Handle publish to profile
  const handlePublishToProfile = async () => {
    // System is now free to use - no plan check needed

    await publishToProfile(
      generatedPost,
      allowNoImage,
      imageSource,
      selectedImages,
      selectedSerperImages,
      geminiGeneratedImage,
      incrementPostedCount
    );
  };

  // Suggest topics
  const suggestTopics = async () => {
    // System is now free to use - no plan check needed

    await suggestTopicsLogic(
      requirements,
      selectedProjectType,
      selectedContentType,
      selectedPublishingPlatform,
      targetCustomer,
      setIsSuggestingTopics,
      setSuggestedTopics
    );
  };

  // Handle save post
  const handleSavePost = async () => {
    const result = await savePost(
      generatedPost,
      editedPostText,
      selectedSerperImages,
      geminiGeneratedImage,
      selectedProjectType,
      selectedContentType,
      selectedPublishingPlatform,
      targetCustomer,
      requirements,
      postLength,
      postStyles,
      includeContactInfo,
      allowNoImage,
      imageSource,
      referenceUrl,
      setIsSaving,
      setSavedPostsCount
    );

    return result;
  };

  // ✅ ADD: useCallback cho navigateToProfile để tránh re-render
  const navigateToProfile = useCallback(() => {
    navigate("/profile");
  }, [navigate]);

  // ✅ ADD: useCallback cho removeContextFile để tránh re-render
  const removeContextFile = useCallback(
    (index) => {
      setContextFiles(contextFiles.filter((_, i) => i !== index));
    },
    [contextFiles]
  );

  // Xử lý khi người dùng bắt đầu kéo file vào khu vực dropzone
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter((prevCount) => prevCount + 1);
    setIsDragging(true);
  };

  // Xử lý khi người dùng kéo file qua khu vực dropzone
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // Xử lý khi người dùng kéo file ra khỏi khu vực dropzone
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter((prevCount) => prevCount - 1);
    if (dragCounter - 1 === 0) {
      setIsDragging(false);
    }
  };

  // Xử lý khi người dùng thả file vào khu vực dropzone
  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragCounter(0);
    setIsDragging(false);

    // Lấy danh sách các file được thả
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;

    // Kiểm tra số lượng file hiện tại và file mới
    if (contextFiles.length + files.length > 5) {
      toast.error("Chỉ cho phép tối đa 5 file tài liệu tham khảo", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Tính tổng kích thước hiện tại của các file đã có
    let currentTotalSize = 0;
    contextFiles.forEach((file) => {
      if (file.file && file.file.size) {
        currentTotalSize += file.file.size;
      }
    });

    // Tính tổng kích thước của các file mới
    let newFilesTotalSize = 0;
    files.forEach((file) => {
      newFilesTotalSize += file.size;
    });

    // Kiểm tra nếu tổng kích thước vượt quá giới hạn 10MB
    const maxTotalSize = 10 * 1024 * 1024; // 10MB
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      toast.error(
        `Tổng kích thước file (${(
          (currentTotalSize + newFilesTotalSize) /
          (1024 * 1024)
        ).toFixed(2)}MB) vượt quá giới hạn 10MB`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      return;
    }

    // Sử dụng lại hàm xử lý file đã có
    await handleFiles(
      files,
      contextFiles,
      setContextFiles,
      setIsProcessingContext
    );
  };

  // Xử lý khi người dùng chọn file từ input
  const handleContextFileChange = async (e) => {
    const files = Array.from(e.target.files);

    // Kiểm tra số lượng file hiện tại và file mới
    if (contextFiles.length + files.length > 5) {
      toast.error("Chỉ cho phép tối đa 5 file tài liệu tham khảo", {
        position: "top-right",
        autoClose: 5000,
      });
      // Reset input file để có thể chọn lại cùng một file
      e.target.value = null;
      return;
    }

    // Tính tổng kích thước hiện tại của các file đã có
    let currentTotalSize = 0;
    contextFiles.forEach((file) => {
      if (file.file && file.file.size) {
        currentTotalSize += file.file.size;
      }
    });

    // Tính tổng kích thước của các file mới
    let newFilesTotalSize = 0;
    files.forEach((file) => {
      newFilesTotalSize += file.size;
    });

    // Kiểm tra nếu tổng kích thước vượt quá giới hạn 10MB
    const maxTotalSize = 10 * 1024 * 1024; // 10MB
    if (currentTotalSize + newFilesTotalSize > maxTotalSize) {
      toast.error(
        `Tổng kích thước file (${(
          (currentTotalSize + newFilesTotalSize) /
          (1024 * 1024)
        ).toFixed(2)}MB) vượt quá giới hạn 10MB`,
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      // Reset input file để có thể chọn lại cùng một file
      e.target.value = null;
      return;
    }

    await handleFiles(
      files,
      contextFiles,
      setContextFiles,
      setIsProcessingContext
    );
    // Reset input file để có thể chọn lại cùng một file
    e.target.value = null;
  };

  // ✅ FIXED: Effect để set default values khi domainConfig sẵn sàng
  useEffect(() => {
    if (domainConfig && domainConfig.defaults && !isLoading) {
      console.log(
        "🔄 NormalPostGenerator: Setting initial defaults from domainConfig:",
        {
          domainId: domainConfig.id,
          domainName: domainConfig.name,
          selectedDomain,
          configMatchesDomain: domainConfig.id === selectedDomain,
          defaults: domainConfig.defaults,
          currentProjectType: selectedProjectType,
          currentContentType: selectedContentType,
          currentPostLength: postLength,
          currentPostStyles: postStyles,
          hasSavedDataLoaded: hasSavedDataLoaded,
        }
      );

      // ✅ FIXED: Only proceed if domainConfig matches the currently selected domain
      if (domainConfig.id !== selectedDomain) {
        console.log(
          `⚠️ NormalPostGenerator: Config mismatch - domainConfig.id: ${domainConfig.id}, selectedDomain: ${selectedDomain}. Waiting for sync...`
        );
        return;
      }

      // ✅ FIXED: Only set defaults if no saved data was loaded
      if (!hasSavedDataLoaded) {
        // ✅ FIXED: Set defaults if current values are empty OR if they are real-estate defaults but we're in different domain
        const isCurrentlyRealEstateDefaults =
          selectedProjectType === "Dự án khu đô thị (hỗn hợp)" &&
          selectedContentType === "Câu chuyện thương hiệu về dự án";

        const shouldSetDefaults =
          !selectedProjectType ||
          !selectedContentType ||
          (isCurrentlyRealEstateDefaults && domainConfig.id !== "real-estate");

        // For "other" domain, don't set project type and content type (they should be empty)
        if (domainConfig.id === "other") {
          setSelectedProjectType(""); // Always empty for "other" domain
          setSelectedContentType(""); // Always empty for "other" domain
          console.log(
            "✅ Set empty project type and content type for 'other' domain"
          );
        } else {
          if (shouldSetDefaults || !selectedProjectType) {
            setSelectedProjectType(domainConfig.defaults.projectType);
            console.log(
              "✅ Set default project type:",
              domainConfig.defaults.projectType
            );
          }

          if (shouldSetDefaults || !selectedContentType) {
            setSelectedContentType(domainConfig.defaults.contentType);
            console.log(
              "✅ Set default content type:",
              domainConfig.defaults.contentType
            );
          }
        }

        if (
          shouldSetDefaults ||
          (postLength === DEFAULT_POST_LENGTH &&
            domainConfig.defaults.postLength)
        ) {
          setPostLength(domainConfig.defaults.postLength);
          console.log(
            "✅ Set default post length:",
            domainConfig.defaults.postLength
          );
        }

        if (
          shouldSetDefaults ||
          (postStyles.length === 1 &&
            postStyles[0] === DEFAULT_POST_STYLES[0] &&
            domainConfig.defaults.postStyles)
        ) {
          setPostStyles(domainConfig.defaults.postStyles);
          console.log(
            "✅ Set default post styles:",
            domainConfig.defaults.postStyles
          );
        }
      } else {
        console.log(
          "⏭️ NormalPostGenerator: Skipping domain defaults because saved data was loaded"
        );
      }
    } else {
      console.log(
        "⏳ NormalPostGenerator: Waiting for domainConfig to be ready...",
        {
          hasDomainConfig: !!domainConfig,
          hasDefaults: !!(domainConfig && domainConfig.defaults),
          isLoading,
          hasSavedDataLoaded: hasSavedDataLoaded,
        }
      );
    }
  }, [
    domainConfig,
    isLoading,
    hasSavedDataLoaded,
    refreshKey, // ✅ Add refreshKey to ensure re-run when domain changes
    // ✅ FIXED: Remove current state from dependencies to avoid infinite loop
    // selectedProjectType,
    // selectedContentType,
    // postLength,
    // postStyles,
  ]);

  // Handle domain change - reset form to new domain defaults
  const handleDomainChange = (newDomainId) => {
    console.log(
      `🔄 NormalPostGenerator: Handling domain change to: ${newDomainId}`
    );
    console.log(`🔄 Current domainConfig before change:`, domainConfig);

    // ✅ FIXED: Sử dụng switchDomain từ hook thay vì gọi getDomainConfig trực tiếp
    switchDomain(newDomainId);

    // ✅ FIXED: Lấy config mới sau khi switch
    const newDomainConfig = getDomainConfig(newDomainId);

    if (newDomainConfig && newDomainConfig.defaults) {
      console.log(`✅ New domain config loaded:`, {
        domain: newDomainConfig.name,
        defaults: newDomainConfig.defaults,
        projectTypesCount: Object.keys(newDomainConfig.projectTypes || {})
          .length,
        contentTypesCount: Object.keys(newDomainConfig.contentTypes || {})
          .length,
      });

      // ✅ FIXED: Sử dụng setTimeout để đảm bảo state update sau khi domain đã switch
      setTimeout(() => {
        // Reset form fields to new domain defaults
        // For "other" domain, don't set project type and content type (they should be empty)
        if (newDomainConfig.id === "other") {
          setSelectedProjectType(""); // Always empty for "other" domain
          setSelectedContentType(""); // Always empty for "other" domain
        } else {
          setSelectedProjectType(newDomainConfig.defaults.projectType);
          setSelectedContentType(newDomainConfig.defaults.contentType);
        }
        setPostLength(
          newDomainConfig.defaults.postLength || DEFAULT_POST_LENGTH
        );
        setPostStyles(
          newDomainConfig.defaults.postStyles || DEFAULT_POST_STYLES
        );

        // Clear user inputs
        setRequirements("");
        setTargetCustomer("");
        setReferenceUrl("");

        // Clear uploaded files và context
        setContextFiles([]);
        setSelectedImages([]);

        // Clear suggested topics
        setSuggestedTopics([]);

        // Reset image source và allowNoImage về default
        setImageSource("internet");
        setAllowNoImage(false);

        // Reset contact info về default
        setIncludeContactInfo(false);

        // Clear generated post data để tránh confusion
        setEditedPostText("");
        setHasEdited(false);
        setSelectedSerperImages([]);

        // Close all dropdowns
        setActiveDropdown(null);

        console.log(
          `✅ Form reset completed for domain: ${newDomainConfig.name}`
        );
      }, 50); // ✅ Small delay để đảm bảo domain config đã được update

      console.log(
        `✅ Domain change completed successfully to: ${newDomainConfig.name}`
      );
    } else {
      console.error(`❌ Failed to load config for domain: ${newDomainId}`);
      toast.error("Có lỗi khi chuyển lĩnh vực hoạt động. Vui lòng thử lại.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-full mx-auto px-2 md:px-4 sm:px-3 md:sm:px-6 pt-3 md:pt-6 pb-4 md:pb-8">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 text-center">
            TẠO BÀI VIẾT TỰ ĐỘNG
          </h1>
        </div>

        {/* Daily Limit Warning */}
        <DailyLimitWarning
          currentCount={dailyPostCount || 0}
          showRetention={true}
        />

        <div className="flex flex-col md:flex-row gap-3 md:gap-6">
          {/* Phần bên trái - Form nhập liệu */}
          <div className="w-full md:w-3/5">
            <div className="bg-white rounded-md shadow-md p-3 md:p-6">
              {/* Thông tin cơ bản */}
              <AccordionSection
                title="Thông tin cơ bản"
                isExpanded={expandedSections.basicInfo}
                toggleExpand={(e) => toggleSection("basicInfo", e)}
              >
                <BasicInfoSection
                  selectedProjectType={selectedProjectType}
                  setSelectedProjectType={setSelectedProjectType}
                  selectedContentType={selectedContentType}
                  setSelectedContentType={setSelectedContentType}
                  selectedPublishingPlatform={selectedPublishingPlatform}
                  setSelectedPublishingPlatform={setSelectedPublishingPlatform}
                  isProjectTypeDropdownOpen={activeDropdown === "projectType"}
                  setIsProjectTypeDropdownOpen={(value) =>
                    setActiveDropdown(value ? "projectType" : null)
                  }
                  isContentTypeDropdownOpen={activeDropdown === "contentType"}
                  setIsContentTypeDropdownOpen={(value) =>
                    setActiveDropdown(value ? "contentType" : null)
                  }
                  isPublishingPlatformDropdownOpen={
                    activeDropdown === "publishingPlatform"
                  }
                  setIsPublishingPlatformDropdownOpen={(value) =>
                    setActiveDropdown(value ? "publishingPlatform" : null)
                  }
                  projectTypeDropdownRef={projectTypeDropdownRef}
                  contentTypeDropdownRef={contentTypeDropdownRef}
                  publishingPlatformDropdownRef={publishingPlatformDropdownRef}
                  toggleProjectTypeDropdown={() => {
                    const newValue = activeDropdown !== "projectType";
                    setActiveDropdown(newValue ? "projectType" : null);
                  }}
                  toggleContentTypeDropdown={() => {
                    const newValue = activeDropdown !== "contentType";
                    setActiveDropdown(newValue ? "contentType" : null);
                  }}
                  togglePublishingPlatformDropdown={() => {
                    const newValue = activeDropdown !== "publishingPlatform";
                    setActiveDropdown(newValue ? "publishingPlatform" : null);
                  }}
                  handleProjectTypeSelect={(projectType) => {
                    setSelectedProjectType(projectType);
                    setActiveDropdown(null);
                  }}
                  handleContentTypeSelect={(contentType) => {
                    setSelectedContentType(contentType);
                    setActiveDropdown(null);
                  }}
                  handlePublishingPlatformSelect={(platform) => {
                    const platformId = PublishingPlatforms[platform];
                    setSelectedPublishingPlatform({
                      text: platform,
                      id: platformId,
                    });
                    setActiveDropdown(null);
                  }}
                  isAutoPostGenerator={false}
                  onDomainChange={handleDomainChange}
                  domainConfig={domainConfig}
                  refreshKey={refreshKey}
                />
              </AccordionSection>

              {/* Thông tin dự án */}
              <AccordionSection
                key={`project-info-${
                  domainConfig?.id || "default"
                }-${refreshKey}`}
                title={
                  domainConfig?.labels?.projectInfoLabel || "Thông tin dự án"
                }
                isExpanded={expandedSections.projectInfo}
                toggleExpand={(e) => toggleSection("projectInfo", e)}
              >
                <ProjectInfoSection
                  key={`${domainConfig?.id || "default"}-${refreshKey}`}
                  requirements={requirements}
                  setRequirements={setRequirements}
                  targetCustomer={targetCustomer}
                  setTargetCustomer={setTargetCustomer}
                  referenceUrl={referenceUrl}
                  setReferenceUrl={setReferenceUrl}
                  suggestedTopics={suggestedTopics}
                  isSuggestingTopics={isSuggestingTopics}
                  handleTopicSelect={handleTopicSelect}
                  suggestTopics={suggestTopics}
                  clearSuggestedTopics={clearSuggestedTopics}
                  suggestedTopicsRef={suggestedTopicsRef}
                  domainConfig={domainConfig}
                />
                {/* File context */}
                <div className="mb-4">
                  <div className="mb-1">
                    <label className="block text-sm font-medium text-gray-700 mt-4">
                      Tài liệu tham khảo
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mb-1">
                    Tối đa 5 file, mỗi file không quá 2MB, tổng dung lượng không
                    quá 10MB
                  </p>
                  <div
                    className={`mt-1 flex justify-center px-3 md:px-6 pt-3 md:pt-5 pb-3 md:pb-6 border-2 ${
                      isDragging
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 hover:bg-gray-50"
                    } border-dashed rounded-md cursor-pointer transition-colors duration-150`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleDropzoneClick}
                  >
                    <div className="space-y-1 text-center">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="context-file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                        >
                          <span>Tải lên tài liệu</span>
                          <input
                            id="context-file-upload"
                            name="context-file-upload"
                            type="file"
                            className="sr-only"
                            multiple
                            ref={contextFileInputRef}
                            onChange={handleContextFileChange}
                            accept=".pdf,.txt,.jpg,.jpeg,.png"
                          />
                        </label>
                        <p className="pl-1">hoặc kéo và thả</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PDF, TXT, JPG, PNG
                      </p>
                    </div>
                  </div>

                  {/* Hiển thị danh sách file đã tải lên */}
                  {contextFiles.length > 0 && (
                    <div className="mt-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-1">
                        Tài liệu đã tải lên:
                      </h4>
                      <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                        {contextFiles.map((file, index) => (
                          <li
                            key={index}
                            className="pl-2 md:pl-3 pr-3 md:pr-4 py-2 md:py-3 flex items-center justify-between text-sm"
                          >
                            <div className="w-0 flex-1 flex items-center">
                              {file.type === "image" ? (
                                <svg
                                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              ) : (
                                <svg
                                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                              <span className="ml-2 flex-1 w-0 truncate">
                                {file.name}
                              </span>
                            </div>
                            <div className="ml-4 flex-shrink-0">
                              <button
                                onClick={() => removeContextFile(index)}
                                className="font-medium text-red-600 hover:text-red-500"
                              >
                                Xóa
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isProcessingContext && (
                    <div className="mt-2 text-sm text-gray-500">
                      <div className="flex items-center">
                        <FiRefreshCw className="animate-spin mr-1" />
                        <span>Đang xử lý tài liệu...</span>
                      </div>
                    </div>
                  )}
                </div>
              </AccordionSection>

              {/* Tùy chỉnh & Phương tiện */}
              <AccordionSection
                title="Tùy chỉnh & Phương tiện"
                isExpanded={expandedSections.customization}
                toggleExpand={(e) => toggleSection("customization", e)}
              >
                <div>
                  {/* Độ dài bài viết */}
                  <div className="mb-3 md:mb-4">
                    <label className="block text-gray-700 font-medium mb-1 md:mb-2">
                      Độ dài bài viết
                    </label>
                    <div className="flex w-full rounded-md shadow-sm">
                      {POST_LENGTHS.map((length, index) => (
                        <button
                          key={length.value}
                          type="button"
                          className={`flex-1 py-2 px-2 text-center text-sm font-medium border
                            ${index === 0 ? "rounded-l-md border-r-0" : ""}
                            ${
                              index === POST_LENGTHS.length - 1
                                ? "rounded-r-md"
                                : index > 0
                                ? "border-r-0"
                                : ""
                            }
                            ${
                              postLength === length.value
                                ? "bg-blue-600 text-white border-blue-600 z-10 relative"
                                : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
                            }`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log(
                              "🔘 Button clicked:",
                              length.value,
                              "Current postLength:",
                              postLength
                            );
                            setPostLength(length.value);
                          }}
                        >
                          {length.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Style bài viết */}
                  <div className="mb-3 md:mb-4">
                    <label className="block text-gray-700 font-medium mb-1 md:mb-2">
                      Phong cách bài viết
                    </label>
                    <div className="flex flex-wrap gap-1 md:gap-2">
                      {POST_STYLES.map((style) => {
                        const styleId =
                          typeof style === "string"
                            ? style.toLowerCase().replace(/\s+/g, "-")
                            : style.value.toLowerCase().replace(/\s+/g, "-");
                        const styleLabel =
                          typeof style === "string" ? style : style.label;
                        const styleValue =
                          typeof style === "string" ? style : style.value;

                        return (
                          <button
                            key={styleId}
                            type="button"
                            className={`py-1 md:py-2 px-3 md:px-4 rounded-full text-sm font-medium
                              ${
                                postStyles.includes(styleValue)
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                              }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Thay đổi từ mảng thành phần tử đơn lẻ
                              setPostStyles([styleValue]);
                            }}
                          >
                            {styleLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Nguồn ảnh */}
                  <div className="mb-3 md:mb-4">
                    <ImageSourceSelector
                      imageSource={imageSource}
                      setImageSource={setImageSource}
                      allowNoImage={allowNoImage}
                      setAllowNoImage={setAllowNoImage}
                      selectedImages={selectedImages}
                      setSelectedImages={setSelectedImages}
                      contextFiles={contextFiles}
                      setContextFiles={setContextFiles}
                      contextFileInputRef={contextFileInputRef}
                      handleContextFileChange={handleContextFileChange}
                      handleDragEnter={handleDragEnter}
                      handleDragOver={handleDragOver}
                      handleDragLeave={handleDragLeave}
                      handleDrop={handleDrop}
                      handleDropzoneClick={handleDropzoneClick}
                      isDragging={isDragging}
                      removeContextFile={removeContextFile}
                      isProcessingContext={isProcessingContext}
                    />
                  </div>

                  {/* Thêm thông tin liên hệ */}
                  <div className="mb-3 md:mb-4">
                    <div className="flex items-center justify-between p-2 md:p-3 border border-gray-200 rounded-md bg-white shadow-sm">
                      <label className="text-gray-700 font-medium">
                        Thêm thông tin liên hệ
                      </label>
                      <div className="relative inline-block align-middle select-none">
                        <input
                          type="checkbox"
                          name="contactInfo"
                          id="contactInfo"
                          checked={includeContactInfo}
                          onChange={() =>
                            setIncludeContactInfo(!includeContactInfo)
                          }
                          className="hidden"
                        />
                        <label
                          htmlFor="contactInfo"
                          className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out cursor-pointer"
                          style={{
                            backgroundColor: includeContactInfo
                              ? "#3b82f6"
                              : "#e5e7eb",
                          }}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out ${
                              includeContactInfo
                                ? "translate-x-6"
                                : "translate-x-1"
                            }`}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionSection>

              {/* Nút tạo bài viết */}
              <div className="flex justify-center mt-4 md:mt-8">
                <button
                  type="button"
                  className={`flex items-center py-2 md:py-3 px-4 md:px-6 rounded-md font-medium text-white ${
                    isGeneratingPost
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  } transition-colors shadow-md`}
                  onClick={handleGeneratePost}
                  disabled={isGeneratingPost}
                >
                  {isGeneratingPost ? (
                    <>
                      <FiRefreshCw className="animate-spin mr-2" />
                      Đang tạo...
                    </>
                  ) : (
                    <>
                      <FiSend className="mr-2" />
                      Tạo bài viết
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Phần bên phải - Kết quả */}
          <div className="w-full md:w-2/5">
            {generatedPost ? (
              <GeneratedPost
                generatedPost={generatedPost}
                generatedPostRef={generatedPostRef}
                isEditMode={isEditMode}
                toggleEditMode={toggleEditMode}
                editedPostText={editedPostText}
                setEditedPostText={setEditedPostText}
                setHasEdited={setHasEdited}
                isRegeneratingPost={isRegeneratingPost}
                isRegeneratingImage={isRegeneratingImage}
                regeneratePost={handleRegeneratePost}
                regenerateImages={handleRegenerateImages}
                serperImages={serperImages}
                selectedSerperImages={selectedSerperImages}
                setSelectedSerperImages={setSelectedSerperImages}
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                geminiGeneratedImage={geminiGeneratedImage}
                imageSource={imageSource}
                allowNoImage={allowNoImage}
                isPublishing={isPublishing}
                publishPost={handlePublishPost}
                publishToProfile={handlePublishToProfile}
                selectedFanpages={selectedFanpages}
                fanpages={fanpages}
                isDropdownOpen={isDropdownOpen}
                toggleDropdown={toggleDropdown}
                handleFanpageSelect={handleFanpageSelect}
                showFacebookPublishing={
                  selectedPublishingPlatform &&
                  selectedPublishingPlatform.id === 1
                }
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                setIsDropdownOpen={setIsDropdownOpen}
                hasFanpages={hasFanpages}
                navigateToProfile={navigateToProfile}
                generatedPosts={generatedPosts}
                selectedPostIndex={selectedPostIndex}
                selectPost={selectPost}
                isSaving={isSaving}
                savePost={handleSavePost}
                savedPostsCount={savedPostsCount}
                dropdownStyle={dropdownStyle}
              />
            ) : (
              <div className="bg-white rounded-md shadow-md p-3 md:p-5 flex flex-col items-center justify-center min-h-[300px]">
                <div className="text-center text-gray-500 mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-16 w-16 mx-auto mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                    />
                  </svg>
                  <p className="text-lg font-medium">
                    Bài viết của bạn sẽ hiển thị ở đây
                  </p>
                </div>
                <p className="text-gray-500 text-center max-w-md">
                  Điền thông tin vào form bên trái và nhấn "Tạo bài viết" để bắt
                  đầu.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer {...getOptimizedToastContainerProps()} />
    </div>
  );
};

export default NormalPostGenerator;
